import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Compute days remaining in the 30-day tag-along window from an episode publish date.
export function daysLeft(episodeDate) {
  if (!episodeDate) return 30;
  try {
    const start = new Date(episodeDate).getTime();
    const elapsedDays = (Date.now() - start) / 86400000;
    return Math.max(0, Math.ceil(30 - elapsedDays));
  } catch {
    return 30;
  }
}

export function TagAlongWindowStatus({ episode, className = "" }) {
  const left = daysLeft(episode.episode_date);
  const total = Number(episode.tag_along_total || 0);
  if (left <= 0) {
    return (
      <div className={`text-xs font-mono uppercase tracking-widest text-black/70 ${className}`}>
        Opportunity closed{total > 0 ? <> · Total <span className="font-bold text-black">€{total.toLocaleString()}</span></> : null}
      </div>
    );
  }
  return (
    <div className={`text-xs font-mono uppercase tracking-widest text-black/70 ${className}`}>
      <span className="text-black font-bold tabular-nums">{left}</span> {left === 1 ? "day" : "days"} left
      {total > 0 ? <> · Tagged along <span className="font-bold text-black">€{total.toLocaleString()}</span></> : null}
    </div>
  );
}

const inputCls = "bg-black border-white/10 rounded-none text-white focus-visible:ring-lime";

export default function TagAlongDialog({ episode, trigger, triggerTestId = "tag-along-open-btn", mode = "tag_along" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ investor_name: "", email: "", phone: "", company: "", linkedin: "", amount_eur: 5000, message: "" });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const isSolo = mode === "solo_invest";

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseInt(form.amount_eur);
    if (!amt || amt < 5000) { toast.error("Minimum ticket is €5,000"); return; }
    setBusy(true);
    try {
      const endpoint = isSolo ? "/solo-invest" : "/tag-along";
      await api.post(endpoint, { ...form, episode_id: episode.id, amount_eur: amt });
      toast.success(isSolo ? "Interest recorded. Simone will connect you with the founder." : "Interest recorded. Simone and the founder will be in touch.");
      setOpen(false);
      setForm({ investor_name: "", email: "", phone: "", company: "", linkedin: "", amount_eur: 5000, message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  };

  const left = daysLeft(episode.episode_date);

  const defaultTrigger = (
    <button data-testid={triggerTestId} onClick={() => setOpen(true)} className="w-full py-3 bg-black text-lime font-heading font-black uppercase tracking-wider hover:bg-black/80 transition-colors">
      {isSolo ? "I wish to invest anyway →" : "Tag along →"}
    </button>
  );

  return (
    <>
      {trigger ? trigger(() => setOpen(true)) : defaultTrigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={isSolo ? "solo-invest-modal" : "tag-along-modal"} className="max-w-xl bg-surface border-white/10 rounded-none text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tighter">
              {isSolo ? `Invest in ${episode.startup_name} (solo)` : `Tag along on ${episode.startup_name}`}
            </DialogTitle>
          </DialogHeader>
          {isSolo ? (
            <p className="text-white/60 text-sm">
              The host <span className="text-white font-semibold">did not invest</span> in this deal. Terms are negotiated directly with the founder — you're on your own, not co-investing with Simone. Minimum €5,000. Simone will only forward your interest to the startup.
            </p>
          ) : (
            <p className="text-white/60 text-sm">Same valuation, same terms as the €20K host check. Minimum €5,000. Window closes {left} {left === 1 ? "day" : "days"} after publication.</p>
          )}
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Name *</Label>
              <Input data-testid="ta-name" required value={form.investor_name} onChange={set("investor_name")} className={`mt-2 ${inputCls}`} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Email *</Label>
                <Input data-testid="ta-email" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Phone *</Label>
                <Input data-testid="ta-phone" required value={form.phone} onChange={set("phone")} className={`mt-2 ${inputCls}`} placeholder="+352 …" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Company / Fund</Label>
                <Input data-testid="ta-company" value={form.company} onChange={set("company")} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">LinkedIn</Label>
                <Input data-testid="ta-linkedin" value={form.linkedin} onChange={set("linkedin")} className={`mt-2 ${inputCls}`} placeholder="https://linkedin.com/in/…" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Amount (€) * <span className="text-white/30">min 5,000</span></Label>
              <Input data-testid="ta-amount" required type="number" min="5000" step="500" value={form.amount_eur} onChange={set("amount_eur")} className={`mt-2 ${inputCls}`} />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Message (optional)</Label>
              <Textarea data-testid="ta-message" rows={3} value={form.message} onChange={set("message")} className={`mt-2 ${inputCls}`} />
            </div>
            <p data-testid="ta-disclaimer" className="text-white/50 text-xs font-mono uppercase tracking-widest leading-relaxed border-l-2 border-lime pl-3">
              {isSolo
                ? "Your interest will be shared with the founder of the company. Simone is not a party to this transaction and will not co-invest."
                : "Your interest will be shared with the founder of the company so we can sync up together on the tag-along investment."}
            </p>
            <button data-testid="ta-submit" type="submit" disabled={busy} className="w-full py-3 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.02] transition-transform disabled:opacity-50">
              {busy ? "Submitting…" : "Submit interest →"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
